// Donor-portal preferences API helper — CHG-1513 portal-pivot retarget.
//
// Backend: portal.py canonical at GET + PATCH /api/v1/my-giving/preferences
// (DonorCommunicationPreferences schema). Replaces the CHG-1490 (α.6)
// shim.
//
// CHG-2945 (Slice I, donor-portal self-service remediation) — org-scope +
// display_preference parity with v1:
//   - Both GET and PATCH now forward an optional `org_id` query param.
//     The backend treats a missing org_id as an ALL-ORG fan-out write
//     (GivingService.update_communication_preferences) — the v2 page
//     previously always wrote all-org silently while v1 forced per-org
//     selection. The page now always passes an explicit org_id.
//   - `display_preference` (CHG-2885 / DRIFT-1122) joins the contract:
//     the donor's OWN privacy choice for how their name renders on donor
//     walls / live feeds (org_default / full_name / first_last_initial /
//     anonymous).
//   - `PreferredChannel` corrected to the backend PreferredChannel enum
//     (models/donor.py: email | phone | mail | none) — the previous
//     "sms"/"push"/"postal" values would fail the service's enum
//     validation on write.
//   - `fetchMyOrganizations` reads GET /my-giving/organizations (orgs
//     where the donor holds a linked supporter profile — DON-R9) as the
//     org-scope picker source, mirroring v1's AuthContext.donorProfiles.

import { apiClient } from "./client";

export type PreferredChannel = "email" | "phone" | "mail" | "none";

export interface MePreferences {
  email_opt_in: boolean;
  sms_opt_in: boolean;
  push_opt_in: boolean;
  postal_opt_in: boolean;
  preferred_channel: string;
  annual_report_opt_in: boolean;
  display_preference: string;
}

export interface MePreferencesPatch {
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
  push_opt_in?: boolean;
  postal_opt_in?: boolean;
  preferred_channel?: string;
  annual_report_opt_in?: boolean;
  display_preference?: string;
}

/** Minimal projection of PortalOrgSummary — the scope picker needs id + name. */
export interface MyPreferenceOrg {
  id: string;
  name: string;
}

export async function fetchMyOrganizations(): Promise<MyPreferenceOrg[]> {
  const r = await apiClient().get<MyPreferenceOrg[]>(
    "/my-giving/organizations",
  );
  return r.data;
}

export async function fetchMePreferences(
  orgId?: string,
): Promise<MePreferences> {
  const r = await apiClient().get<MePreferences>("/my-giving/preferences", {
    params: orgId ? { org_id: orgId } : undefined,
  });
  return r.data;
}

export async function patchMePreferences(
  patch: MePreferencesPatch,
  orgId?: string,
): Promise<MePreferences> {
  const r = await apiClient().patch<MePreferences>(
    "/my-giving/preferences",
    patch,
    { params: orgId ? { org_id: orgId } : undefined },
  );
  return r.data;
}
